/**
 * DOC-02 / D-05 — pure filename → doc_type heuristic.
 *
 * Suggests a `doc_type` from a dropped file's name so the review screen can
 * pre-fill the document-card Select. This is a SUGGESTION, never an
 * auto-decision: the dealer always confirms or changes it on the review
 * screen (UI-SPEC: "Guessed from filename — confirm or change"). When no
 * confident match is found it returns `undefined` — the Select stays at its
 * placeholder rather than guessing wrong.
 *
 * Case-insensitive, substring-based, pure, and overridable. The 12-value
 * `doc_type` enum is the authority (StoreDocumentRequest::DOC_TYPES); this
 * heuristic only ever returns values within that enum.
 */

/** The 12 business document types (mirror StoreDocumentRequest::DOC_TYPES). */
export const DOC_TYPES = [
  'invoice',
  'coo_boat',
  'coo_trailer',
  'bill_of_lading',
  'bill_of_sale',
  'survey',
  'registration',
  'insurance',
  'service_record',
  'recall_notice',
  'warranty',
  'other',
] as const;

export type DocType = (typeof DOC_TYPES)[number];

/**
 * Ordered match rules. The FIRST rule whose keyword appears in the lowercased
 * filename wins. Trailer must be checked before the generic origin/coo rule so
 * "trailer-coo.pdf" maps to coo_trailer, not coo_boat.
 */
const HEURISTIC_RULES: ReadonlyArray<{ test: (lower: string) => boolean; docType: DocType }> = [
  { test: (s) => s.includes('invoice'), docType: 'invoice' },
  // Trailer COO needs a certificate-of-origin signal too, so a filename like
  // "trailer-registration.pdf" is NOT mis-typed as a trailer COO (WR-03).
  // "trailer-coo.pdf" / "trailer-cert.pdf" still match here (and trailer wins
  // over the generic coo_boat rule below because this rule is checked first).
  {
    test: (s) => s.includes('trailer') && ['coo', 'origin', 'cert'].some((k) => s.includes(k)),
    docType: 'coo_trailer',
  },
  { test: (s) => s.includes('origin') || s.includes('coo'), docType: 'coo_boat' },
  { test: (s) => s.includes('bol') || s.includes('lading'), docType: 'bill_of_lading' },
];

/**
 * Suggest a doc_type from a filename. Returns `undefined` when there is no
 * confident guess (the caller leaves the Select at its placeholder).
 */
export function docTypeHeuristic(filename: string): DocType | undefined {
  if (!filename) {
    return undefined;
  }

  const lower = filename.toLowerCase();

  for (const rule of HEURISTIC_RULES) {
    if (rule.test(lower)) {
      return rule.docType;
    }
  }

  return undefined;
}
